import { useCallback, useEffect, useRef, useState } from "react";
import type { RevealState } from "../types";

const REVEAL_DELAY = 1500;
const COLLAPSE_DELAY = 3500;

type UseAutoRevealOptions = {
	/** Delay after the page is ready before revealing. */
	revealDelay?: number;
	/** How long the trigger stays revealed before collapsing again. */
	collapseDelay?: number;
};

const isPageReady = (): boolean =>
	typeof document !== "undefined" && document.readyState === "complete";

/**
 * Starts collapsed. Once the page has loaded, reveals after `revealDelay`,
 * then collapses again after `collapseDelay`. `reveal()` restarts the cycle manually.
 */
export const useAutoReveal = ({
	revealDelay = REVEAL_DELAY,
	collapseDelay = COLLAPSE_DELAY,
}: UseAutoRevealOptions = {}) => {
	const [state, setState] = useState<RevealState>("collapsed");
	const revealTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
	const collapseTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	const clearTimers = useCallback(() => {
		clearTimeout(revealTimeoutRef.current);
		clearTimeout(collapseTimeoutRef.current);
	}, []);

	const scheduleCollapse = useCallback(() => {
		collapseTimeoutRef.current = setTimeout(() => setState("collapsed"), collapseDelay);
	}, [collapseDelay]);

	const reveal = useCallback(() => {
		clearTimers();
		setState("revealed");
		scheduleCollapse();
	}, [clearTimers, scheduleCollapse]);

	const collapse = useCallback(() => {
		clearTimers();
		setState("collapsed");
	}, [clearTimers]);

	useEffect(() => {
		const scheduleReveal = () => {
			revealTimeoutRef.current = setTimeout(() => {
				setState("revealed");
				scheduleCollapse();
			}, revealDelay);
		};

		if (isPageReady()) {
			scheduleReveal();
		} else {
			window.addEventListener("load", scheduleReveal, { once: true });
		}

		return () => {
			window.removeEventListener("load", scheduleReveal);
			clearTimers();
		};
	}, [revealDelay, scheduleCollapse, clearTimers]);

	return { state, reveal, collapse };
};
